import { StringContext } from "../StringContext";
import { ScrollController } from "./ScrollController";

/**
 * Frame interval assumed before the engine has measured its own, in ms.
 *
 * The lerp advances per frame rather than per millisecond, so a duration is
 * converted into a pace using the frame interval actually being achieved —
 * a page running at 30fps needs half the per-frame speed of one at 60fps to
 * take the same wall-clock time. This value only covers the first call, before
 * any frame has been timed.
 */
const FALLBACK_FRAME_MS = 1000 / 60;

/** Frame intervals outside this range are treated as measurement noise. */
const MIN_FRAME_MS = 1000 / 240;
const MAX_FRAME_MS = 1000 / 20;

/** Frames the engine takes to settle after the step drops below the threshold. */
const SETTLE_FRAMES = 2;

/** Bounds for the solved lerp speed: slow enough to crawl, fast enough to snap. */
const MIN_SCROLL_TO_SPEED = 0.0005;
const MAX_SCROLL_TO_SPEED = 0.9;

/** Bisection steps; 60 halvings resolve the speed far below display precision. */
const SOLVER_ITERATIONS = 60;

type SmoothComputeStepResult = {
  current: number;
  target: number;
  delta: number;
  lerped: number;
  scrollForce: number;
  absVelocity: number;
};

export class StringScrollSmooth extends ScrollController {
  public readonly name: string = "smooth";

  private scrollForce: number = 0.0;
  private wheelImpulse: number = 0.0;
  private previousCurrent: number = 0.0;
  private scrollToSequence: number = 0;
  private activeScrollToId: number | null = null;

  /**
   * Lerp speed for the running `scrollTo`, or null to use the configured one.
   *
   * A timed `scrollTo` does not animate the position itself — it only picks the
   * speed at which the existing lerp settles, so the motion stays the engine's
   * own and keeps its easing, damping, direction tracking and interruption.
   */
  private scrollToSpeed: number | null = null;

  private readonly stepResult: SmoothComputeStepResult = {
    current: 0.1,
    target: 0.1,
    delta: 0.1,
    lerped: 0.1,
    scrollForce: 0.1,
    absVelocity: 0.1,
  };

  constructor(context: StringContext) {
    super(context);
    this.stepResult.current = 0.0;
    this.stepResult.target = 0.0;
    this.stepResult.delta = 0.0;
    this.stepResult.lerped = 0.0;
    this.stepResult.scrollForce = 0.0;
    this.stepResult.absVelocity = 0.0;
  }

  public override setPosition(position: number): void {
    super.setPosition(position);
    this.scrollForce = 0.0;
    this.wheelImpulse = 0.0;
    this.previousCurrent = position;
    this.activeScrollToId = null;
    this.scrollToSpeed = null;
  }

  public stopScroll(): void {
    const scrollData = this.context.data.scroll;

    scrollData.lerped = 0.0;
    scrollData.delta = 0.0;
    scrollData.target = scrollData.current;
    this.onCalcUpdate();
    this.isProg = false;
    this.clearScrollingClasses();
    this._scrollDirState = -1;
    this._lastAppliedDirState = -1;
    this.activeScrollToId = null;
    this.scrollToSpeed = null;
  }

  public onFrame(): void {
    const scrollData = this.context.data.scroll;

    if (scrollData.delta !== 0.0) {
      this.computeStep(
        scrollData.current,
        scrollData.target,
        scrollData.delta,
        this.scrollToSpeed ?? scrollData.speed,
        scrollData.speedAccelerate,
        scrollData.decelerationCoefficient,
        scrollData.maxDelta,
        scrollData.bottomPosition,
        this.stepResult,
      );

      this.scrollForce = this.stepResult.scrollForce;
      scrollData.target = this.stepResult.target;
      scrollData.delta = this.stepResult.delta;
      scrollData.lerped = this.stepResult.lerped;
      scrollData.current = this.stepResult.current;

      const scale = this.context.data.viewport.transformScale;
      scrollData.transformedCurrent =
        scale !== 1.0 ? scrollData.current * scale : scrollData.current;

      this.updateScrollDirection(scrollData.lerped > 0.0);

      if (this.stepResult.absVelocity < scrollData.stopThreshold) {
        scrollData.current = Math.round(scrollData.target);
        this.previousCurrent = scrollData.current;
        this.onCalcUpdate();
        this.stopScroll();
        this.onScrollStop();
      } else {
        this.isProg = true;

        if (this.previousCurrent !== scrollData.current) {
          this.previousCurrent = scrollData.current;
          this.onCalcUpdate();
        }
      }
    }
  }

  /**
   * Keys the browser would natively scroll with. In smooth mode the engine owns
   * scrollTop, so letting the browser handle these makes it fight the lerp: the
   * native jump moves scrollTop, `onScroll` ignores it while `isProg` is true,
   * and the next frame snaps the page back. We preventDefault and feed the
   * equivalent impulse through the same delta pipeline the wheel uses.
   */
  private isTypingTarget(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el || !el.tagName) return false;
    const tag = el.tagName.toUpperCase();
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
    // Shift+key is not a plain scroll gesture — the browser uses it to extend a
    // selection, and applications bind their own shortcuts to it. Claiming it
    // here would also fight any such handler that answers the same press by
    // calling scrollTo. Shift+Space is the one real exception: it is the long
    // established "page up", so it is handled below.
    if (e.shiftKey && e.key !== " ") return;
    if (this.isTypingTarget(e.target)) return;

    const scrollData = this.context.data.scroll;
    const viewport =
      this.context.data.viewport.containerHeight || this.context.data.viewport.windowHeight;
    const lineStep = viewport * 0.12;

    let impulse = 0;
    let absolute: number | null = null;

    switch (e.key) {
      case "ArrowDown":
        impulse = lineStep;
        break;
      case "ArrowUp":
        impulse = -lineStep;
        break;
      case "PageDown":
        impulse = viewport * 0.9;
        break;
      case "PageUp":
        impulse = -viewport * 0.9;
        break;
      case " ":
        impulse = e.shiftKey ? -viewport * 0.9 : viewport * 0.9;
        break;
      case "Home":
        absolute = 0;
        break;
      case "End":
        absolute = scrollData.bottomPosition;
        break;
      default:
        return;
    }

    e.preventDefault();
    this.scrollToSpeed = null;

    if (absolute !== null) {
      this.scrollTo(absolute);
      return;
    }

    if (scrollData.delta === 0.0) {
      this.onScrollStart();
    }

    const isUp = impulse < 0;
    const atTop = scrollData.target <= 0.0 && isUp;
    const atBottom = scrollData.target >= scrollData.bottomPosition && !isUp;

    if (!atTop && !atBottom) {
      scrollData.delta += impulse * scrollData.multiplier;
    }
  };

  public enableScrollEvents(): void {
    window.addEventListener("keydown", this.onKeyDown, { passive: false });
  }

  public disableScrollEvents(): void {
    window.removeEventListener("keydown", this.onKeyDown);
  }

  public onWheel(e: WheelEvent): void {
    if (e.deltaY !== 0.0) {
      e.preventDefault();
    }

    // The user is driving again: drop the pace a timed scrollTo picked so the
    // wheel behaves at the configured speed.
    this.scrollToSpeed = null;

    this.wheelImpulse = e.deltaY;
    if (this.wheelImpulse === 0.0) return;

    const scrollData = this.context.data.scroll;

    if (scrollData.delta === 0.0) {
      this.onScrollStart();
    }

    const isScrollingUp = this.wheelImpulse < 0;
    const atTop = scrollData.target === 0.0 && isScrollingUp;
    const atBottom = scrollData.target === scrollData.bottomPosition && !isScrollingUp;

    if (!atTop && !atBottom) {
      scrollData.delta += this.wheelImpulse * scrollData.multiplier;
    }
  }

  public onScroll(e: Event): void {
    if (!this.isProg) {
      const scrollData = this.context.data.scroll;
      const nativeScrollTop = scrollData.elementContainer.scrollTop;
      const positionDelta = nativeScrollTop - scrollData.current;

      scrollData.current = nativeScrollTop;
      scrollData.target = nativeScrollTop;
      scrollData.delta = 0.0;
      scrollData.lerped = positionDelta;
      scrollData.displacement = 0.0;

      const scale = this.context.data.viewport.transformScale;
      scrollData.transformedCurrent = scale !== 1.0 ? nativeScrollTop * scale : nativeScrollTop;

      this.scrollForce = 0.0;
      this.wheelImpulse = 0.0;
      this.isProg = false;
      this.previousCurrent = nativeScrollTop;
      this.activeScrollToId = null;
      this.scrollToSpeed = null;

      if (positionDelta !== 0.0) {
        this.updateScrollDirection(positionDelta > 0.0);
        this.triggerScrollRules();
      }
    }
  }

  public deactivate(): void {
    super.deactivate();
    this.scrollToSpeed = null;
    this.scrollForce = 0.0;
    this.wheelImpulse = 0.0;
    this.previousCurrent = this.context.data.scroll.current;
  }

  public scrollTo(position: number, immediate?: boolean, duration?: number) {
    const scrollData = this.context.data.scroll;
    const scrollToId = ++this.scrollToSequence;
    this.activeScrollToId = scrollToId;

    this.scrollToSpeed =
      immediate || duration === undefined || duration <= 0
        ? null
        : this.speedForDuration(Math.abs(position - scrollData.current), duration);

    if (immediate) {
      scrollData.current = position;
      scrollData.target = position;
      scrollData.delta = 0.0;
      scrollData.lerped = 0.0;

      const scale = this.context.data.viewport.transformScale;
      scrollData.transformedCurrent = scale !== 1.0 ? position * scale : position;

      this.onCalcUpdate();
      return;
    }

    scrollData.target = position;
    scrollData.delta = 1.0;
  }

  private computeStep(
    current: number,
    target: number,
    delta: number,
    speed: number,
    speedAccelerate: number,
    decayPower: number,
    maxDelta: number,
    bottomPosition: number,
    out: SmoothComputeStepResult,
  ): void {
    const clampedDelta = this.clamp(delta, -maxDelta, maxDelta);
    const scrollForce = clampedDelta * speedAccelerate;
    const decayFactor = Math.pow(1 - speedAccelerate, decayPower);

    const nextTarget = Math.min(Math.max(0.0, target + scrollForce), bottomPosition);
    const lerped = (nextTarget - current) * speed;
    const nextDelta = clampedDelta * decayFactor;

    const nextCurrent = current + lerped;

    out.current = nextCurrent;
    out.target = nextTarget;
    out.delta = nextDelta;
    out.lerped = lerped;
    out.scrollForce = scrollForce;
    out.absVelocity = Math.abs(lerped);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  /**
   * Lerp speed that lands a `distance` jump in roughly `durationMs`.
   *
   * Each frame the lerp closes a fixed fraction `s` of the remaining gap, so
   * after `n` frames the step size is `distance * s * (1 - s)^n`. The scroll is
   * considered settled once that falls under `stopThreshold`, which gives
   *
   *   n = ln(stopThreshold / (distance * s)) / ln(1 - s)
   *
   * That cannot be rearranged for `s` in closed form, so it is solved by
   * bisection — `n` falls monotonically as `s` grows, so the search is stable,
   * and a few dozen iterations of pure arithmetic cost nothing next to the
   * seconds of scrolling that follow.
   */
  private speedForDuration(distance: number, durationMs: number): number | null {
    const scrollData = this.context.data.scroll;
    const threshold = scrollData.stopThreshold;

    // Nothing to pace: no distance, or a threshold that is already met.
    if (distance <= 0 || threshold <= 0 || distance <= threshold) return null;

    // How many frames the requested duration actually buys at the rate this
    // page is running. Using a fixed 60 here would make a 30fps page arrive in
    // half the time asked for.
    const frameMs = this.clamp(
      this.context.data.time.delta || FALLBACK_FRAME_MS,
      MIN_FRAME_MS,
      MAX_FRAME_MS,
    );

    // The engine settles a couple of frames after the step drops below the
    // threshold, so aim slightly short rather than overshooting the request.
    const targetFrames = Math.max(1, durationMs / frameMs - SETTLE_FRAMES);

    let low = MIN_SCROLL_TO_SPEED;
    let high = MAX_SCROLL_TO_SPEED;

    for (let i = 0; i < SOLVER_ITERATIONS; i++) {
      const mid = (low + high) / 2;
      const frames = Math.log(threshold / (distance * mid)) / Math.log(1 - mid);

      // More frames than asked for means the pace is too slow.
      if (frames > targetFrames) low = mid;
      else high = mid;
    }

    return (low + high) / 2;
  }
}
